import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications,
  markAsRead,
  markAllAsRead,
} from '../../services/notificationService';

const CATEGORY_COLORS = {
  UPLOAD: 'var(--teal)',
  EDIT: 'var(--gold)',
  APPROVAL: 'var(--amber)',
  SYSTEM: 'var(--purple)',
};

const TABS = ['All', 'Unread', 'Read'];

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.max(0, Math.floor(diffMs / 1000));
  if (sec < 60) return `${sec} second${sec === 1 ? '' : 's'} ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`;
  const week = Math.floor(day / 7);
  if (week < 4) return `${week} week${week === 1 ? '' : 's'} ago`;
  const month = Math.floor(day / 30);
  if (month < 12) return `${month} month${month === 1 ? '' : 's'} ago`;
  const year = Math.floor(day / 365);
  return `${year} year${year === 1 ? '' : 's'} ago`;
}

export default function NotificationDrawer({ open, onClose }) {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('All');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { rows } = await getNotifications();
      setNotifs(rows);
    } catch {
      setNotifs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    if (open) { load(); setActiveTab('All'); }
  }, [open, load]);

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {
      // non-fatal: leave state unchanged
    }
  };

  const handleClick = (n) => {
    // Navigate only — no auto-mark-as-read
    if (n.action_url) {
      onClose();
      navigate(n.action_url);
    }
  };

  const handleToggleRead = async (e, n) => {
    e.stopPropagation();
    const newState = !n.is_read;
    try {
      await markAsRead(n.id, newState);
      setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, is_read: newState } : x));
    } catch {
      // non-fatal
    }
  };

  const filtered = activeTab === 'All'
    ? notifs
    : activeTab === 'Unread'
      ? notifs.filter(n => !n.is_read)
      : notifs.filter(n => n.is_read);

  const showMarkAll = activeTab !== 'Read';

  return (
    <div className={`notif-drawer${open ? ' open' : ''}`}>
      <div className="nd-hd">
        <div className="nd-title">Notifications</div>
        <div className="d-flex gap-2 align-items-center">
          {showMarkAll && (
            <button className="btn btn-s btn-xs" onClick={handleMarkAllRead}>Mark all read</button>
          )}
          <button className="tb-btn" style={{ width: 28, height: 28 }} onClick={onClose}>✕</button>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 4, padding: '8px 16px 4px', borderBottom: '1px solid #eef3f7' }}>
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '4px 14px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 500,
              border: activeTab === tab ? 'none' : '1px solid #dee2e6',
              background: activeTab === tab ? 'var(--teal)' : 'transparent',
              color: activeTab === tab ? '#fff' : 'var(--t3)',
              cursor: 'pointer',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="nd-body">
        {loading && notifs.length === 0 && (
          <div style={{ padding: 20, color: 'var(--t3)', fontSize: 12.5 }}>Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ padding: 20, color: 'var(--t3)', fontSize: 12.5 }}>
            {activeTab === 'All' ? 'No new notifications' : `No ${activeTab.toLowerCase()} notifications`}
          </div>
        )}
        {filtered.map(n => (
          <div
            key={n.id}
            className={`ni${n.is_read ? '' : ' unread'}`}
            style={{ '--ni-accent': CATEGORY_COLORS[n.category] || 'var(--teal)' }}
            onClick={() => handleClick(n)}
          >
            <div className="ni-type" style={{ color: CATEGORY_COLORS[n.category] || 'var(--teal)' }}>
              {n.category ? `${n.category}` : 'Notification'} · {timeAgo(n.created_at)}
            </div>
            <div className="ni-title">{n.title}</div>
            {n.message && <div className="ni-body">{n.message}</div>}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
              {n.action_url ? (
                <div className="ni-actions">
                  <button
                    className="btn btn-s btn-xs"
                    onClick={(e) => { e.stopPropagation(); handleClick(n); }}
                  >
                    {n.action_label || 'View →'}
                  </button>
                </div>
              ) : <span />}
              <button
                onClick={(e) => handleToggleRead(e, n)}
                title={n.is_read ? 'Mark as unread' : 'Mark as read'}
                style={{
                  background: 'transparent',
                  border: 0,
                  cursor: 'pointer',
                  padding: '2px 4px',
                  fontSize: 14,
                  lineHeight: 1,
                  color: n.is_read ? 'var(--t3)' : (CATEGORY_COLORS[n.category] || 'var(--teal)'),
                }}
              >
                <i className={n.is_read ? 'bi bi-circle' : 'bi bi-circle-fill'} />
              </button>
            </div>
            <div className="ni-time">{timeAgo(n.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
